import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Send,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Heart,
  Sun,
  Image as ImageIcon,
  Volume2,
  VolumeX,
  User,
  Bot,
  AlertCircle,
} from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher, apiClient } from '../api/client'

interface ChatMessage {
  id: number
  message: string
  is_from_user: boolean
  emotion_detected?: string | null
  audio_url?: string | null
  created_at: string
}

export default function ChatSimulator() {
  const { t } = useTranslation()
  const { selectedElderlyId, language } = useAgentStore()
  const queryClient = useQueryClient()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingChunksRef = useRef<Blob[]>([])

  const [inputText, setInputText] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [useStream, setUseStream] = useState(false)
  const [audioPlayingId, setAudioPlayingId] = useState<number | null>(null)
  const [streamedText, setStreamedText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)

  const isRTL = language === 'ar' || language === 'he' || language === 'fa'

  /* ───────── Queries ───────── */
  const {
    data: history,
    isLoading: historyLoading,
  } = useQuery<ChatMessage[]>({
    queryKey: ['chat-history', selectedElderlyId],
    queryFn: () =>
      fetcher<{ messages: ChatMessage[] }>(`/agent/history/${selectedElderlyId}`).then(
        (res) => res.messages
      ),
    enabled: !!selectedElderlyId,
  })

  /* ───────── Mutations ───────── */
  const sendTextMessage = useMutation({
    mutationFn: async (text: string) => {
      const response = await apiClient.post<ChatMessage>(
        `/agent/chat/${selectedElderlyId}`,
        null,
        { params: { message: text } }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history'] })
      setInputText('')
    },
  })

  const sendVoiceMessage = useMutation({
    mutationFn: async (audioBlob: Blob) => {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')

      const response = await apiClient.post<{
        text: string
        audio_url: string
        emotion_detected: string | null
      }>(`/agent/voice/chat/${selectedElderlyId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history'] })
    },
  })

  const dailyCheckin = useMutation({
    mutationFn: () =>
      apiClient.post<ChatMessage>(`/agent/checkin/${selectedElderlyId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat-history'] }),
  })

  const memoryPrompt = useMutation({
    mutationFn: () =>
      apiClient.post<ChatMessage>(`/agent/memory-prompt/${selectedElderlyId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat-history'] }),
  })

  /* ───────── Streaming Chat ───────── */
  const sendStreamMessage = useCallback(async (text: string) => {
    setStreamedText('')
    setIsStreaming(true)
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/agent/chat/stream/${selectedElderlyId}?message=${encodeURIComponent(text)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${useAgentStore.getState().accessToken || ''}`,
          },
        }
      )

      if (!response.body) return

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              queryClient.invalidateQueries({ queryKey: ['chat-history'] })
              setStreamedText('')
              return
            }
            fullText += data
            setStreamedText(fullText)
          }
        }
      }
    } finally {
      setIsStreaming(false)
    }
  }, [selectedElderlyId, queryClient])

  /* ───────── Voice Recording ───────── */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      recordingChunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(recordingChunksRef.current, { type: 'audio/webm' })
        sendVoiceMessage.mutate(audioBlob)
        stream.getTracks().forEach((track) => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
    } catch (err) {
      console.error('Microphone access denied:', err)
      alert(t('chat.mic_error'))
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setRecordingTime(0)
    }
  }

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isRecording) {
      interval = setInterval(() => setRecordingTime((t) => t + 1), 1000)
    }
    return () => clearInterval(interval)
  }, [isRecording])

  /* ───────── Scroll to Bottom ───────── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [history, streamedText])

  /* ───────── Audio Playback ───────── */
  const toggleAudio = (msgId: number, url?: string | null) => {
    if (!url) return
    if (audioPlayingId === msgId) {
      audioRef.current?.pause()
      setAudioPlayingId(null)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = url
        audioRef.current.play()
        setAudioPlayingId(msgId)
        audioRef.current.onended = () => setAudioPlayingId(null)
      }
    }
  }

  /* ───────── Helpers ───────── */
  const handleSend = () => {
    if (!inputText.trim() || !selectedElderlyId) return
    if (useStream) {
      sendStreamMessage(inputText)
      setInputText('')
    } else {
      sendTextMessage.mutate(inputText)
    }
  }

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString(language, {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getEmotionIcon = (emotion?: string | null) => {
    switch (emotion) {
      case 'happy':
      case 'joy':
        return <Sparkles size={14} className="text-warning-500" />
      case 'sad':
      case 'grief':
        return <Heart size={14} className="text-danger-500" />
      case 'calm':
      case 'peaceful':
        return <Sun size={14} className="text-success-500" />
      default:
        return null
    }
  }

  /* ───────── Render Guards ───────── */
  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('chat.no_elderly_selected')}
      </div>
    )
  }

  const messages = history || []
  const isBusy =
    sendTextMessage.isPending ||
    sendVoiceMessage.isPending ||
    dailyCheckin.isPending ||
    memoryPrompt.isPending ||
    isStreaming

  /* ───────── Component ───────── */
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('chat.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {t('chat.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => dailyCheckin.mutate()}
            disabled={dailyCheckin.isPending || isBusy}
            className="px-4 py-3 rounded-xl font-semibold bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300 hover:bg-success-100 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {dailyCheckin.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Sun size={18} />
            )}
            {t('chat.checkin')}
          </button>
          <button
            onClick={() => memoryPrompt.mutate()}
            disabled={memoryPrompt.isPending || isBusy}
            className="px-4 py-3 rounded-xl font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 hover:bg-primary-100 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {memoryPrompt.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <ImageIcon size={18} />
            )}
            {t('chat.memory_prompt')}
          </button>
          <label className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useStream}
              onChange={(e) => setUseStream(e.target.checked)}
              className="w-5 h-5 accent-primary-600"
            />
            <span className="font-semibold text-sm">{t('chat.stream_mode')}</span>
          </label>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto card mb-4 space-y-4">
        {historyLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Loader2 size={32} className="animate-spin mx-auto mb-4" />
            {t('common.loading')}
          </div>
        ) : messages.length === 0 && !streamedText ? (
          <div className="text-center py-16 text-gray-400">
            <Bot size={64} className="mx-auto mb-4 opacity-30" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t('chat.empty_title')}
            </h3>
            <p>{t('chat.empty_desc')}</p>
          </div>
        ) : (
          <>
            {[...messages].reverse().map((msg) => {
              const isUser = msg.is_from_user
              return (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${
                    isUser ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      isUser
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {isUser ? <User size={20} /> : <Bot size={20} />}
                  </div>

                  {/* Bubble */}
                  <div
                    className={`max-w-[75%] rounded-2xl px-5 py-3 ${
                      isUser
                        ? 'bg-primary-600 text-white rounded-se-none'
                        : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white rounded-ss-none'
                    }`}
                  >
                    <p className="text-lg leading-relaxed whitespace-pre-wrap">
                      {msg.message}
                    </p>
                    <div
                      className={`flex items-center gap-2 mt-2 text-xs ${
                        isUser
                          ? 'text-primary-100'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <span>{formatTime(msg.created_at)}</span>
                      {!isUser && msg.emotion_detected && (
                        <span className="flex items-center gap-1">
                          {getEmotionIcon(msg.emotion_detected)}
                          {t(`emotions.${msg.emotion_detected}`) || msg.emotion_detected}
                        </span>
                      )}
                      {!isUser && msg.audio_url && (
                        <button
                          onClick={() => toggleAudio(msg.id, msg.audio_url)}
                          className="p-1 rounded hover:bg-white/20 transition-colors"
                        >
                          {audioPlayingId === msg.id ? (
                            <VolumeX size={14} />
                          ) : (
                            <Volume2 size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}

            {/* Streaming Message */}
            {streamedText && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 flex items-center justify-center">
                  <Bot size={20} />
                </div>
                <div className="max-w-[75%] rounded-2xl px-5 py-3 bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white rounded-ss-none">
                  <p className="text-lg leading-relaxed whitespace-pre-wrap">
                    {streamedText}
                    <span className="inline-block w-2 h-5 bg-primary-500 animate-pulse ms-1 align-middle" />
                  </p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Recording Overlay */}
      {isRecording && (
        <div className="mb-4 p-4 rounded-xl bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3 text-danger-700 dark:text-danger-300">
            <Mic size={24} />
            <span className="font-bold text-lg">
              {t('chat.recording')} ({recordingTime}s)
            </span>
          </div>
          <button
            onClick={stopRecording}
            className="btn-danger flex items-center gap-2"
          >
            <MicOff size={18} />
            {t('chat.stop_recording')}
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="flex gap-3">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isBusy || !selectedElderlyId}
          className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
            isRecording
              ? 'bg-danger-500 text-white animate-pulse'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
          } disabled:opacity-50`}
          title={t('chat.voice')}
        >
          {isRecording ? <MicOff size={24} /> : <Mic size={24} />}
        </button>

        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !isBusy && handleSend()}
          placeholder={t('chat.placeholder')}
          disabled={isBusy || isRecording}
          className="input-field flex-1"
        />

        <button
          onClick={handleSend}
          disabled={isBusy || !inputText.trim() || isRecording}
          className="btn-primary flex-shrink-0 w-14 h-14 p-0 flex items-center justify-center"
        >
          {sendTextMessage.isPending || isStreaming ? (
            <Loader2 size={24} className="animate-spin" />
          ) : (
            <Send size={24} />
          )}
        </button>
      </div>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} className="hidden" />

      {/* Error Display */}
      {(sendTextMessage.isError || sendVoiceMessage.isError) && (
        <div className="mt-3 p-3 rounded-xl bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300 flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          {t('chat.send_error')}
        </div>
      )}
    </div>
  )
}